"use client"

import { useState, useEffect } from "react"
import { API_BASE_URL } from "../../config"
import AdminProductCard from "./AdminProductCard"
import ProductModal from "./ProductModal"
import VariantModal from "./VariantModal"
import ModifiersList from "./ModifiersList"
import ModifierModal from "./ModifierModal"

const AdminDashboard = ({ onLogout }) => {
  const [products, setProducts] = useState([])
  const [selectedCategory, setSelectedCategory] = useState("Appetizers") // Changed from "All" to "Appetizers"
  const [loading, setLoading] = useState(true)
  const [showProductModal, setShowProductModal] = useState(false)
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [activeTab, setActiveTab] = useState("products")
  const [modifiers, setModifiers] = useState([])
  const [editingModifier, setEditingModifier] = useState(null)
  const [showModifierModal, setShowModifierModal] = useState(false)

  const categoryList = [
    // Removed "All" option
    "Appetizers",
    "Salads",
    "Charcoal Grill",
    "Family Platters",
    "Parsley Bowls",
    "Make Your Own Bowl",
    "Sandwiches",
    "Parsley Wraps",
    "Make Your Own Wrap",
    "Pastries",
    "Desserts",
    "Beverages",
  ]

  useEffect(() => {
    fetchProducts()
    fetchModifiers()
  }, [])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/menu`)
      const data = await response.json()

      if (response.ok) {
        setProducts(data.objects || [])
      }
    } catch (error) {
      console.error("Error fetching products:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchModifiers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/modifiers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setModifiers(data.modifiers || [])
      }
    } catch (error) {
      console.error("Error fetching modifiers:", error)
    }
  }

  // Updated filtering logic - always filter by selected category
  const filteredProducts = products.filter(
    (item) => item.type === "ITEM" && item.item_data?.categoryNames?.includes(selectedCategory),
  )

  const handleEditProduct = (product) => {
    setEditingProduct(product)
    setShowProductModal(true)
  }

  const handleAddProduct = () => {
    setEditingProduct(null)
    setShowProductModal(true)
  }

  const handleProductSaved = () => {
    fetchProducts()
    setShowProductModal(false)
    setEditingProduct(null)
  }

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      })

      if (response.ok) {
        fetchProducts()
      } else {
        alert("Failed to delete product")
      }
    } catch (error) {
      console.error("Error deleting product:", error)
      alert("Error deleting product")
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">Parsley Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={onLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("products")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "products"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setActiveTab("modifiers")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "modifiers"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Modifiers
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "products" && (
          <>
            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="category-select" className="text-sm font-medium text-gray-700">
                    Category:
                  </label>
                  <select
                    id="category-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {categoryList.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="text-gray-600">
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? "s" : ""} in {selectedCategory}
                </span>
              </div>
              <button
                onClick={handleAddProduct}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-medium"
              >
                Add Product
              </button>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="text-xl text-gray-600">Loading products...</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <AdminProductCard
                    key={product.id}
                    product={product}
                    onEdit={() => handleEditProduct(product)}
                    onDelete={() => handleDeleteProduct(product.id)}
                  />
                ))}
              </div>
            )}

            {filteredProducts.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-xl text-gray-600 mb-4">No products found in {selectedCategory}</div>
                <p className="text-gray-500 mb-6">There are no products in the {selectedCategory} category yet.</p>
                <button
                  onClick={handleAddProduct}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-md font-medium"
                >
                  Add First Product to {selectedCategory}
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === "modifiers" && (
          <ModifiersList
            modifiers={modifiers}
            onRefresh={fetchModifiers}
            onEdit={(modifier) => {
              setEditingModifier(modifier)
              setShowModifierModal(true)
            }}
            onAdd={() => {
              setEditingModifier(null)
              setShowModifierModal(true)
            }}
          />
        )}
      </div>

      {/* Modals */}
      {showProductModal && (
        <ProductModal product={editingProduct} onClose={() => setShowProductModal(false)} onSave={handleProductSaved} />
      )}

      {showVariantModal && (
        <VariantModal onClose={() => setShowVariantModal(false)} onSave={() => setShowVariantModal(false)} />
      )}

      {showModifierModal && (
        <ModifierModal
          modifier={editingModifier}
          onClose={() => {
            setShowModifierModal(false)
            setEditingModifier(null)
          }}
          onSave={() => {
            fetchModifiers()
            setShowModifierModal(false)
            setEditingModifier(null)
          }}
        />
      )}
    </div>
  )
}

export default AdminDashboard
